import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import api, { getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// Three separate facts, stated where the auditor reads: a verified source is the publisher's exact
// file; a mapping is a person's proposal reviewed by another; compliance is never asserted here.
const SEPARATION = "Sumber terverifikasi berarti berkas sama persis dengan yang dicatat dari penerbit (SHA-256). "
  + "Pemetaan prosedur ke paragraf diusulkan dan direviu oleh orang yang berbeda. Kepatuhan perikatan "
  + "tidak dinilai oleh sistem.";

export default function StandardsLibrary() {
  // Opened from an engagement's menu (Standar › Pustaka Standar): offer the way back to the same panel.
  const [params] = useSearchParams();
  const fromEngagement = params.get("engagement");
  const fromTab = params.get("tab");
  const [sets, setSets] = useState([]);
  const [setId, setSetId] = useState("");
  const [documents, setDocuments] = useState([]);
  const [query, setQuery] = useState("");
  const [sa, setSa] = useState("");
  const [title, setTitle] = useState("");
  const [pageNo, setPageNo] = useState("");
  const [results, setResults] = useState(null);
  const [page, setPage] = useState(null);

  useEffect(() => {
    api.get("/standards/sets").then(r => { setSets(r.data); if (r.data[0]) setSetId(r.data[0].id); })
      .catch(e => toast.error(getErrorMessage(e)));
  }, []);
  useEffect(() => {
    if (!setId) return;
    api.get("/standards/documents", { params: { set_id: setId } }).then(r => setDocuments(r.data))
      .catch(e => toast.error(getErrorMessage(e)));
  }, [setId]);

  // Search by keyword, SA number, title and page, in any combination (roadmap V3 phase 4).
  const search = async () => {
    const params = { set_id: setId };
    if (query.trim().length >= 2) params.q = query.trim();
    if (sa.trim()) params.sa = sa.trim();
    if (title.trim().length >= 2) params.title = title.trim();
    if (!params.q && !params.sa && !params.title) return toast.error("Isi kata kunci, nomor SA atau judul standar.");
    if (pageNo.trim()) params.page = Number(pageNo);
    try { setResults((await api.get("/standards/search", { params })).data.results); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  const openPage = async (versionId, number) => {
    try { setPage((await api.get(`/standards/versions/${versionId}/pages/${number}`)).data); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  // The file has no URL of its own: it is fetched with the user's credentials and shown from memory.
  const openFile = async (versionId) => {
    try {
      const { data } = await api.get(`/standards/versions/${versionId}/file`, { responseType: "blob" });
      window.open(window.URL.createObjectURL(data), "_blank", "noopener");
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <Layout>
      <PageHeader breadcrumb="Metodologi" title="Pustaka Standar" subtitle="Standar Audit sebagai referensi internal"
        actions={fromEngagement ? <Link to={`/engagements/${encodeURIComponent(fromEngagement)}${fromTab && fromTab !== "overview" ? `?tab=${encodeURIComponent(fromTab)}` : ""}`}
          className="text-sm underline" data-testid="standards-back-to-engagement">← Kembali ke perikatan</Link> : null} />
      <div className="p-4 sm:p-6 space-y-4">
        <p className="text-xs text-muted-foreground border border-border rounded-sm bg-white p-3" data-testid="std-separation">{SEPARATION}</p>
        {sets.length === 0 && <p className="text-sm text-muted-foreground" data-testid="std-empty">Belum ada set standar yang diingest. Ingest dilakukan operator di server (scripts/ingest_standards.py).</p>}
        {sets.length > 0 && (
          <div className="flex flex-wrap items-end gap-2">
            <select aria-label="Set standar" className="border border-border rounded-sm h-9 px-2 text-sm bg-white" value={setId} onChange={e => setSetId(e.target.value)} data-testid="std-set">
              {sets.map(s => <option key={s.id} value={s.id}>{s.name} · {s.verified_versions}/{s.documents} terverifikasi</option>)}
            </select>
            <Input className="h-9 w-24 max-w-full" placeholder="SA (mis. 320)" value={sa} onChange={e => setSa(e.target.value)}
              onKeyDown={e => e.key === "Enter" && search()} data-testid="std-sa" aria-label="Nomor SA" />
            <Input className="h-9 w-56 max-w-full" placeholder="Judul standar" value={title} onChange={e => setTitle(e.target.value)}
              onKeyDown={e => e.key === "Enter" && search()} data-testid="std-title" aria-label="Judul standar" />
            <Input className="h-9 w-72 max-w-full" placeholder="Kata kunci dalam standar" value={query} onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && search()} data-testid="std-query" aria-label="Kata kunci" />
            <Input className="h-9 w-20 max-w-full" type="number" min="1" placeholder="Hal." value={pageNo} onChange={e => setPageNo(e.target.value)}
              onKeyDown={e => e.key === "Enter" && search()} data-testid="std-page-no" aria-label="Halaman" />
            <Button size="sm" onClick={search} data-testid="std-search">Cari</Button>
          </div>
        )}
        {results && (
          <div className="space-y-2" data-testid="std-results">
            {results.length === 0 && <p className="text-sm text-muted-foreground">Tidak ada bagian standar yang cocok dengan pencarian ini.</p>}
            {results.map((r, i) => (
              <button key={i} className="block w-full text-left border border-border rounded-sm bg-white p-3 hover:border-slate-500"
                onClick={() => openPage(r.version_id, r.page_number)} data-testid={`std-result-${i}`}>
                <div className="text-xs font-semibold">{r.code} · hlm. {r.page_number}</div>
                <div className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-line">{r.passage}</div>
              </button>
            ))}
          </div>
        )}
        {page && (
          <div className="border border-border rounded-sm bg-white p-3 space-y-2" data-testid="std-page">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-semibold">Halaman {page.number} / {page.page_count}</div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page.number <= 1} onClick={() => openPage(page.version.id, page.number - 1)}>Sebelumnya</Button>
                <Button size="sm" variant="outline" disabled={page.number >= page.page_count} onClick={() => openPage(page.version.id, page.number + 1)}>Berikutnya</Button>
                <Button size="sm" variant="outline" onClick={() => openFile(page.version.id)} data-testid="std-open-file">Buka PDF</Button>
              </div>
            </div>
            {page.extraction === "NO_TEXT_LAYER" ? <p className="text-xs text-amber-800">Halaman ini tidak memiliki lapisan teks; buka PDF untuk membacanya.</p>
              : <pre className="text-xs whitespace-pre-wrap break-words font-sans">{page.text}</pre>}
          </div>
        )}
        <div className="overflow-x-auto border border-border rounded-sm bg-white">
          <table className="w-full text-xs">
            <thead><tr className="text-left border-b border-border"><th className="p-2">Standar</th><th className="p-2">Judul</th><th className="p-2">Sumber</th><th className="p-2">Halaman</th><th className="p-2"></th></tr></thead>
            <tbody>
              {documents.map(d => (
                <tr key={d.id} className="border-b border-border last:border-0" data-testid={`std-doc-${d.number}`}>
                  <td className="p-2 font-medium whitespace-nowrap">{d.code}</td>
                  <td className="p-2">{d.title}</td>
                  <td className="p-2 whitespace-nowrap">{d.current_version ? (d.current_version.source_verification === "VERIFIED_AGAINST_MANIFEST" ? "Terverifikasi" : "Belum terverifikasi") : "Berkas belum tersedia"}</td>
                  <td className="p-2">{d.current_version?.page_count ?? "—"}</td>
                  <td className="p-2">{d.current_version && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => openPage(d.current_version.id, 1)}>Baca</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
